import { Form, Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { DatePicker } from "../../components/DatePicker";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import Select, { Option } from "../../components/Select";
import { useState } from "react";
import { useCreateScreeningRecord } from "../../data/useCreateScreeningRecord";
import { useMembershipDivisionTeam } from "../../hooks/useMembershipDivisionTeam";
import { NewCompanyChecksFrame } from "./NewCompanyChecksFrame";
import { useCountries } from "../../data/useCountries";
import { Box } from "../../components/Box";
import { useEntityTypes } from "../../data/useEntityTypes";
import { DateTime } from "luxon";
import { showError } from "../../utils/error-handling";
import { companyCheckValidationSchema } from "../../utils/validationSchema";

const rowProps: any = {
  flexWrap: "wrap",
  gap: "16",
  my: "4",
};

const inputProps = {
  minWidth: 200,
  flex: 1,
};

const Header = () => {
  return (
    <>
      <H2>Company Details</H2>
      <Paragraph>Please add the company to perform the screening on.</Paragraph>
    </>
  );
};

export const AddCompanyDetails = () => {
  const [doubleClick, setDoubleClick] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkpointIds = location.state?.checkpointIds;

  const isSanctionsSelected = location.state?.isSanctionsSelected;

  const isAdverseMediaSelected = location.state?.isAdverseMediaSelected;

  const { data: entityTypes } = useEntityTypes();
  const { createScreeningRecord, isLoading } = useCreateScreeningRecord();

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();

  const { data: countries } = useCountries();

  const entityTypeOptions = entityTypes?.data.map(({ id, name }) => ({
    label: name,
    value: id,
  })) as Option[];

  const countryOptions = countries?.data.map(({ id, name }) => ({
    label: name,
    value: id,
  })) as Option[];

  const handleSubmit = (form) => {
    setDoubleClick(true);
    createScreeningRecord(
      {
        checkpointIds,
        subject: "entity",
        primaryDivision: form.division,
        primaryTeam: form.team,
        payload: {
          company_name: form.companyName,
          country: form.country,
          entity_type: form.companyType,
          registration_number: form.registrationNumber,
          date_of_incorporation: form.dateOfIncorporation
            ? DateTime.fromJSDate(new Date(form.dateOfIncorporation)).toFormat(
                "yyyy-MM-dd"
              )
            : null,
          external_reference: form.yourReference?.trim() || null,
          // dissolution_date: form.dissolutionDate
          //   ? DateTime.fromJSDate(
          //       new Date(form.dissolutionDate)
          //     ).toFormat("yyyy-MM-dd")
          //   : null,
        },
      },
      {
        onSuccess: ({ mandateId, recordId }) => {
          navigate(`/screening/${mandateId}/company/address/${recordId}`, {
            state: {
              checkpointIds,
              isSanctionsSelected,
              isAdverseMediaSelected,
            },
          });
        },
        onError: (err, stage) => {
          setDoubleClick(false);
          if (stage === "subject") {
            showError(err, "Error creating company.");
          } else if (stage === "mandate") {
            showError(err, "Error creating mandate.");
          } else {
            showError(err, "Error creating mandate type.");
          }
        },
      }
    );
  };

  return (
    <NewCompanyChecksFrame step="company">
      <Formik
        initialValues={{
          yourReference: "",
          division: singleDivisionId,
          team: singleTeamId,
          companyName: "",
          country: "",
          companyType: "",
          registrationNumber: "",
          dateOfIncorporation: "",
          // dissolutionDate: "",
        }}
        validationSchema={companyCheckValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {(props) => (
          <Form onSubmit={props.handleSubmit} style={{ width: "510px" }}>
            <Header />
            <Flex {...rowProps}>
              <Input
                {...inputProps}
                onChange={props.handleChange}
                value={props.values.yourReference}
                placeholder="Your Reference"
                mt="2"
                label="Your reference"
                name="yourReference"
              />
            </Flex>
            {showDivisionField && (
              <Flex {...rowProps}>
                <Select
                  {...inputProps}
                  onChange={(option: any) => {
                    const newDivisionTeams = teamOptionsFor(option.value);
                    props.setValues({
                      ...props.values,
                      division: option.value,
                      team:
                        newDivisionTeams.length === 1
                          ? newDivisionTeams[0].value
                          : "",
                    });
                  }}
                  value={divisionOptions?.find(
                    ({ value }: any) => value === props.values.division
                  )}
                  options={divisionOptions}
                  placeholder="Choose division"
                  label="Division"
                  name="division"
                  isRequired
                  hasError={Boolean(props.errors.division)}
                  errorMessage={props.errors.division as string}
                />
              </Flex>
            )}
            {teamOptionsFor(props.values.division).length > 0 && (
              <Flex {...rowProps}>
                <Select
                  {...inputProps}
                  key={props.values.division}
                  onChange={(option: any) =>
                    props.setFieldValue("team", option.value)
                  }
                  value={
                    teamOptionsFor(props.values.division).find(
                      ({ value }: any) => value === props.values.team
                    ) ?? null
                  }
                  options={teamOptionsFor(props.values.division)}
                  placeholder="Choose team"
                  label="Team"
                  name="team"
                  isRequired
                  hasError={Boolean(props.errors.team)}
                  errorMessage={props.errors.team as string}
                />
              </Flex>
            )}
            <Flex {...rowProps}>
              <Input
                {...inputProps}
                onChange={props.handleChange}
                value={props.values.companyName}
                placeholder="Enter company name"
                mt="2"
                label="Company name"
                name="companyName"
                isRequired
                hasError={Boolean(props.errors.companyName)}
                errorMessage={props.errors.companyName as string}
              />
            </Flex>

            <Flex {...rowProps}>
              <Select
                value={countryOptions?.find(
                  ({ value }) => value === props.values.country
                )}
                options={countryOptions}
                onChange={(option: Option) => {
                  props.setFieldValue("country", option.value);
                }}
                minWidth={200}
                flex={1}
                id="countryOfIncorporation"
                label="Country of incorporation"
                name="country"
                isRequired
                hasError={Boolean(props.errors.country)}
                errorMessage={props.errors.country as string}
              />
            </Flex>
            <Flex {...rowProps}>
              <Select
                value={entityTypeOptions?.find(
                  ({ value }) => value === props.values.companyType
                )}
                options={entityTypeOptions}
                onChange={(option: Option) =>
                  props.setFieldValue("companyType", option.value)
                }
                minWidth={200}
                flex={1}
                label="Company type"
                name="companyType"
                hasError={Boolean(props.errors.companyType)}
                errorMessage={props.errors.companyType as string}
              />
            </Flex>
            <Flex {...rowProps}>
              <Box flex={1}>
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.registrationNumber}
                  placeholder="Enter registration number"
                  label="Registration number"
                  name="registrationNumber"
                  hasError={Boolean(props.errors.registrationNumber)}
                  errorMessage={props.errors.registrationNumber as string}
                />
              </Box>
              <Box flex={1}>
                <DatePicker
                  value={props.values.dateOfIncorporation as any}
                  onChange={(date: any) => {
                    props.setFieldValue("dateOfIncorporation", date);
                  }}
                  maxDate={DateTime.now().toJSDate()}
                  label="Date of incorporation"
                  name="dateOfIncorporation"
                  hasError={Boolean(props.errors.dateOfIncorporation)}
                  errorMessage={props.errors.dateOfIncorporation as string}
                  {...inputProps}
                />
              </Box>
            </Flex>

            <Flex justifyContent="flex-end">
              {/* <Button
                size="large"
                mt="2"
                mb="0"
                variant="secondary"
                onClick={() =>
                  navigate(`/applications/${mandateId}/individuals`)
                }
              >
                Back
              </Button> */}
              <Button
                size="large"
                mt="2"
                mb="0"
                type="submit"
                isDisabled={
                  (props.dirty && !props.isValid) || isLoading || doubleClick
                }
              >
                Next
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </NewCompanyChecksFrame>
  );
};
